import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  TemplateRef,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Modal } from '@spirit-testing/component-library';
import { TranslatePipe } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TestdatenblattService } from '../../../services/testdatenblatt.service';
import { Testdatenblatt } from '../../../models/testdatenblatt.model';

/**
 * Testdatenblatt-Verbinden-Dialog (NEX-524, Design page-module.jsx:498-556):
 * listet die Datenblätter des Projekts (bereits verbundene werden ausgeblendet),
 * erlaubt Suche + Mehrfachauswahl und gibt die gewählten Datenblätter via
 * {@link applied} zurück.
 */
@Component({
  selector: 'app-testdatenblatt-embed-dialog',
  imports: [
    Modal,
    TranslatePipe,
    MatButton,
    MatFormField,
    MatLabel,
    MatInput,
    MatIcon,
    MatCheckbox,
    MatProgressSpinner,
  ],
  templateUrl: './testdatenblatt-embed-dialog.component.html',
  styleUrl: './testdatenblatt-embed-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestdatenblattEmbedDialogComponent implements AfterViewInit {
  private readonly service = inject(TestdatenblattService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private loaded = false;

  readonly projectId = input.required<string>();
  /** IDs bereits verbundener Datenblätter, die nicht wählbar sein dürfen. */
  readonly excludeIds = input<readonly string[]>([]);
  readonly visible = model(false);

  readonly applied = output<Testdatenblatt[]>();
  /**
   * NEX-1050: Sekundärbutton „Neues Datenblatt anlegen" — Parent handelt die
   * Navigation. Der Dialog schließt sich bewusst NICHT: der CanDeactivate-Guard
   * des Modul-Editors kann die Navigation ablehnen (dirty form, Verwerfen-
   * Bestätigung). Wir lassen die Suche + Auswahl stehen und ueberlassen das
   * Schließen dem Parent, sobald der Router den Wechsel bestätigt hat.
   */
  readonly createNew = output<void>();

  @ViewChild('embedContent') embedContentTemplate!: TemplateRef<unknown>;
  resolvedContentTemplate: TemplateRef<unknown> | null = null;

  readonly sheets = signal<Testdatenblatt[]>([]);
  readonly loading = signal(true);
  readonly error = signal(false);
  readonly searchTerm = signal('');
  readonly selectedIds = signal<ReadonlySet<string>>(new Set());

  readonly filtered = computed(() => {
    const exclude = new Set(this.excludeIds());
    const term = this.searchTerm().toLowerCase().trim();
    return this.sheets()
      .filter((s) => !exclude.has(s.id))
      .filter((s) => !term || s.name.toLowerCase().includes(term) || s.key.toLowerCase().includes(term));
  });

  /**
   * Zaehlt die gesamte Auswahl, nicht nur die gerade sichtbare. Eine Suche
   * schraenkt die Sicht ein, nicht die Auswahl — sonst faellt der Zaehler auf 0,
   * der Primaerknopf wird disabled und beim Verbinden fehlt die vorher angehakte
   * Zeile (NEX-1050-Review). Ausgeschlossene Zeilen koennen hier nicht mitzaehlen:
   * Sie werden nie gerendert, also nie angehakt, und {@link close} leert die
   * Auswahl bei jedem Schliessen.
   */
  readonly selectedCount = computed(() => this.selectedIds().size);

  readonly canApply = computed(() => this.selectedCount() > 0);

  constructor() {
    effect(() => {
      if (this.visible() && !this.loaded) {
        this.loaded = true;
        this.loadSheets();
      }
    });
  }

  ngAfterViewInit(): void {
    this.resolvedContentTemplate = this.embedContentTemplate;
    this.cdr.detectChanges();
  }

  onSearch(event: Event): void {
    this.searchTerm.set((event.target as HTMLInputElement).value);
  }

  isSelected(id: string): boolean {
    return this.selectedIds().has(id);
  }

  toggle(id: string): void {
    this.selectedIds.update((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  onApply(): void {
    if (!this.canApply()) return;
    const selected = this.selectedIds();
    this.applied.emit(this.sheets().filter((s) => selected.has(s.id)));
    this.close();
  }

  onCancel(): void {
    this.close();
  }

  /**
   * NEX-1050-Review: Vorher lief hier {@link close} bedingungslos hinter
   * dem Emit — die Route hängt aber am {@code canDeactivateModuleForm}, der bei
   * dirty form eine zweite Bestätigung öffnet. Klickte der Nutzer dort „Abbrechen",
   * blieb er im Modul-Editor mit geschlossenem Dialog: Suche und Häkchen weg,
   * ohne dass irgendwo erklärt wird, warum. Wir emittieren jetzt nur; der Parent
   * schließt uns erst nach erfolgreicher Navigation über {@code [(visible)]}.
   */
  onCreateNew(): void {
    this.createNew.emit();
  }

  private close(): void {
    this.visible.set(false);
    this.searchTerm.set('');
    this.selectedIds.set(new Set());
  }

  private loadSheets(): void {
    this.loading.set(true);
    this.error.set(false);
    this.service
      .list(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (sheets) => {
          this.sheets.set(sheets);
          this.loading.set(false);
        },
        error: () => {
          this.error.set(true);
          this.loading.set(false);
        },
      });
  }
}
